import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';

import { Research, ResearchApiService, ResearchPayload } from '../api/research-api.service';
import { AuthService } from '../auth/auth.service';
import { ResearchSelectionService } from './research-selection.service';

/**
 * Loads the research list and keeps the active research resolved.
 *
 * Resolution order: the user's server-persisted choice (`users.active_research_id`,
 * which follows them across browsers) → this browser's stored selection →
 * the `is_default` research as a fallback on first login.
 */
@Injectable({ providedIn: 'root' })
export class ResearchContextService {
  private api = inject(ResearchApiService);
  private auth = inject(AuthService);
  private selection = inject(ResearchSelectionService);

  readonly researches = signal<Research[]>([]);
  readonly loading = signal(false);
  readonly ready = signal(false);

  readonly activeId = computed(() => this.selection.activeId());
  readonly active = computed<Research | null>(() => {
    const id = this.selection.activeId();
    const list = this.researches();
    return list.find(r => r.id === id) ?? list.find(r => r.is_default) ?? list[0] ?? null;
  });

  constructor() {
    // Follow the server-persisted workspace whenever it arrives or changes.
    // On a restored session `/users/me` resolves after the list has loaded, so
    // the selection has to be re-applied here rather than only in refresh().
    effect(() => {
      const id = this.auth.activeResearchId();
      const ready = this.ready();
      untracked(() => {
        if (!id || !ready) return;
        if (this.researches().some(r => r.id === id)) this.selection.set(id);
      });
    });
  }

  /** Ensure the list is loaded (call once from the app shell). */
  ensureLoaded(): void {
    if (this.ready() || this.loading()) return;
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.api.getAll().subscribe({
      next: (list) => {
        this.researches.set(list);
        this.selection.set(this.resolveActiveId(list));
        this.loading.set(false);
        this.ready.set(true);
      },
      error: () => {
        this.loading.set(false);
        this.ready.set(true);
      },
    });
  }

  /**
   * Server choice → this browser's stored choice → default. Ids that no longer
   * exist (deleted research) are skipped at each step.
   */
  private resolveActiveId(list: Research[]): string | null {
    for (const id of [this.auth.activeResearchId(), this.selection.activeId()]) {
      if (id && list.some(r => r.id === id)) return id;
    }
    return (list.find(r => r.is_default) ?? list[0])?.id ?? null;
  }

  /** Switch the active research and persist it for the current user. */
  setActive(id: string): void {
    this.selection.set(id);
    this.api.setActiveResearch(id).subscribe({
      next: () => this.auth.updateActiveResearch(id),
      error: () => { /* non-fatal — the selection stays local until next login */ },
    });
  }

  create(body: ResearchPayload) {
    return this.api.create(body);
  }
}
